import { CircularProgress, TextField, Grid, Paper, MenuItem, IconButton, Checkbox, FormControlLabel, Menu } from '@mui/material'
import { MoreVert } from '@mui/icons-material'

import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import ButtonBackSave from '../../generic/buttons/floatingButtons/ButtonBackSave'
import { generatePath, useNavigate } from 'react-router'
import Chip from '@mui/material/Chip'
import { SmartSelect } from '../../generic/smartSelect'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AlertUI from '../../generic/AlertUI'
import SkillCapabilitiesDialog from './SkillCapabilitiesDialog'

const WorkerSettings = () => {
  const [worker, setWorker] = useState({})
  const [technologies, setTechnologies] = useState([])
  const [technologyGroups, setTechnologyGroups] = useState([])
  const [calendars, setCalendars] = useState([])
  const [mosCapabilities, setMosCapabilities] = useState([])
  const [selectedTechnologies, setSelectedTechnologies] = useState([])
  const [selectedGroupTechnologies, setSelectedGroupTechnologies] = useState([])
  const [AlertElement, showAlert] = AlertUI()
  const { workerId } = useParams()
  const { t_ } = useSmartTranslation()
  const backSaveButtonRef = useRef()
  const [editMode, setEditMode] = useState(false)

  // D11 (2026-07-15, PO amendment): capacity editing (canPrepare/canExecute per link) moved out
  // of this screen into the "Skills" modal, opened from a ⋮ menu in this accordion's header.
  const [skillsMenuAnchorEl, setSkillsMenuAnchorEl] = useState(null)
  const [skillsDialogOpen, setSkillsDialogOpen] = useState(false)

  const navigate = useNavigate()

  const fetchWorker = async () => {
    const workerDB = await apiGet('worker/' + workerId)
    setWorker(workerDB)
    if (workerDB.allIndividualTechnologies) {
      const initialChips = workerDB.allIndividualTechnologies.map((tech) => ({
        id: tech.id,
        name: tech.name,
        isGroup: false,
        fakeId: `technology_${tech.id}`
      }))

      const initialGroupChips = workerDB.allGroupTechnologies.map((tech) => ({
        id: tech.id,
        name: tech.name,
        isGroup: true,
        fakeId: `group_${tech.id}`
      }))

      setSelectedTechnologies(initialChips)
      setSelectedGroupTechnologies(initialGroupChips)
    }
  }

  // Recupera el worker a cargar + las tecnologías y grupos a mostrar en los chips
  useEffect(() => {
    fetchWorker()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId])

  // Recupera tecnologías individuales
  useEffect(() => {
    const fetchTechnologies = async () => {
      const technologiesDB = await apiGet('technologiesBasicData')
      setTechnologies(technologiesDB)
    }
    fetchTechnologies()
  }, [])

  // Recupera grupos de tecnología con sus correspondientes tecnologías dentro
  useEffect(() => {
    const fetchTechnologyGroups = async () => {
      const technologyGroupsDB = await apiGet('technologyGroupsNoPagination')
      setTechnologyGroups(technologyGroupsDB)
    }
    fetchTechnologyGroups()
  }, [])

  useEffect(() => {
    const fetchCalendars = async () => {
      const calendarsDB = await apiGet('allCalendarReferences')
      setCalendars(calendarsDB)
    }
    fetchCalendars()
  }, [])

  // Recupera el catálogo de MOS capabilities para el multiselect (igual que technologiesBasicData)
  useEffect(() => {
    const fetchMosCapabilities = async () => {
      const mosCapabilitiesDB = await apiGet('mos-capabilities')
      setMosCapabilities(mosCapabilitiesDB)
    }
    fetchMosCapabilities()
  }, [])

  const changeBetweenVisualizeModes = (edit) => {
    setEditMode(edit)
  }

  const goBack = (state) => {
    navigate('/configuration/workers', { replace: true, state })
  }

  // Recupera las tecnologías a mostrar en el autocomplete de selección de tecnologías
  const optionTechnologiesMultiSelect = () => {
    if (technologies === null || technologies === undefined) {
      return []
    } else if (Object.keys(technologies).length === 0) {
      return []
    } else if (technologies !== undefined && technologies !== null && Array.isArray(technologies)) {
      const selectedValues = technologies.map((opt) => {
        return opt.name
      })
      return selectedValues
    } else {
      return []
    }
  }

  // Recupera las tecnologías a mostrar en el autocomplete de selección de grupos de tecnologías
  const optionTechnologyGroupsMultiSelect = () => {
    if (technologyGroups === null || technologyGroups === undefined) {
      return []
    } else if (Object.keys(technologyGroups).length === 0) {
      return []
    } else if (technologyGroups !== undefined && technologyGroups !== null && Array.isArray(technologyGroups)) {
      const selectedValues = technologyGroups.map((opt) => {
        return opt.name
      })
      return selectedValues
    } else {
      return []
    }
  }

  // Carga los autocompletes de tecnologías y grupos que corresponden al worker y actualiza el objeto worker que se envía para el guardado/update
  const selectTechnologiesOrGroups = (isGroup) => {
    if (worker.workerTechnologies === null || worker.workerTechnologies === undefined) {
      return []
    } else if (Object.keys(worker.workerTechnologies).length === 0) {
      return []
    } else if (Array.isArray(worker.workerTechnologies)) {
      const selectedValues = worker.workerTechnologies
        .filter((opt) => opt.isGroup === isGroup)
        .map((opt) => opt.name)
      return selectedValues
    } else {
      return []
    }
  }

  // Tecnologías individuales seleccionadas en el autocomplete de tecnologías y actualiza el objeto worker que se envía para el guardado/update
  const onChangeTechnologyValues = (newTechnologiesSelectedValues) => {
    const existingWorkerTechnologies = worker.workerTechnologies || []
    const newTechnologyValues = newTechnologiesSelectedValues.map((value) => {
      const newTechnologyValue = technologies.find((elem) => elem.name === value)
      // Preserve the phase capability flags of links that were already assigned (RN1); only a
      // genuinely new link falls back to the RN3/V3 default of both capabilities active. Still
      // needed after D11: the Skills modal reads/writes these same flags on worker.workerTechnologies.
      const existingLink = existingWorkerTechnologies.find((link) => !link.isGroup && link.id === newTechnologyValue.id)
      // Historia 121 (S55): the machine scope of an existing link is preserved exactly like its
      // phase flags — the same defect #57 fixed, with another field. A new link is born with the
      // default "all machines" scope (RN3).
      return {
        id: newTechnologyValue.id,
        name: newTechnologyValue.name,
        isGroup: false,
        fakeId: `technology_${newTechnologyValue.id}`,
        canPrepare: existingLink ? existingLink.canPrepare : true,
        canExecute: existingLink ? existingLink.canExecute : true,
        deviceScope: existingLink ? existingLink.deviceScope : 'ALL_DEVICES',
        scopedDevices: existingLink && Array.isArray(existingLink.scopedDevices) ? existingLink.scopedDevices : []
      }
    })

    const updatedWorkerTechnologies = [...newTechnologyValues, ...existingWorkerTechnologies.filter((value) => value.isGroup)]
    setSelectedTechnologies(newTechnologyValues)

    setWorker({ ...worker, workerTechnologies: updatedWorkerTechnologies })
  }

  // Grupos de tecnología seleccionadas en el autocomplete de tecnologías
  const onChangeTechnologyGroupValues = (newTechnologyGroupsSelectedValues) => {
    const existingWorkerTechnologies = worker.workerTechnologies || []
    const selectedGroupTechnologiesBreakdown = []
    const newGroupValues = newTechnologyGroupsSelectedValues.map((value) => {
      const newGroupValue = technologyGroups.find((elem) => elem.name === value)
      // Preserve the phase capability flags of groups that were already assigned (RN1); only a
      // genuinely new group link falls back to the RN3/V3 default of both capabilities active.
      const existingLink = existingWorkerTechnologies.find((link) => link.isGroup && link.id === newGroupValue.id)
      return {
        id: newGroupValue.id,
        name: newGroupValue.name,
        isGroup: true,
        fakeId: `group_${newGroupValue.id}`,
        canPrepare: existingLink ? existingLink.canPrepare : true,
        canExecute: existingLink ? existingLink.canExecute : true
      }
    })

    const updatedWorkerTechnologies = [...newGroupValues, ...existingWorkerTechnologies.filter((value) => !value.isGroup)]

    for (const groupName of newTechnologyGroupsSelectedValues) {
      const group = technologyGroups.find((elem) => elem.name === groupName)

      if (group) {
        // Desglose de grupos de tecnología con las tecnologías correspondientes
        selectedGroupTechnologiesBreakdown.push(
          ...group.technologies.map((tech) => ({
            id: tech.id,
            name: tech.name,
            group: group.name
          }))
        )
      }
    }
    setWorker({ ...worker, workerTechnologies: updatedWorkerTechnologies })
    setSelectedGroupTechnologies(selectedGroupTechnologiesBreakdown)
  }

  const onChangeCalendarValues = (event) => {
    const newCalendarSelectedValue = event.target.value
    const selectedCalendar = calendars.find((calendar) => calendar.id === newCalendarSelectedValue)

    setWorker({ ...worker, calendar: selectedCalendar })
  }

  // MOS capabilities — multi-select like skills/technologies, persisted under the same
  // MANAGE_WORKER_SKILLS gate. The catalogue (options) comes from GET /mos-capabilities; the worker's
  // selection is `worker.mosCapabilities` ([{id, code, name}], mirrors allIndividualTechnologies) and
  // is sent verbatim on apiPut('worker') — the backend extracts the ids and writes the join table.
  const mosCapabilityOptions = mosCapabilities.map((capability) => capability.name)
  const selectedMosCapabilities = (worker.mosCapabilities || []).map((capability) => capability.name)
  const onChangeMosCapabilities = (selectedCapabilityNames) => {
    const selectedCapabilities = selectedCapabilityNames
      .map((capabilityName) => mosCapabilities.find((capability) => capability.name === capabilityName))
      .filter((capability) => capability !== undefined)
    setWorker({ ...worker, mosCapabilities: selectedCapabilities })
  }

  // Single-machine presence restriction. `singleMachinePresence` is a non-nullable boolean and
  // `singleMachinePresenceMode` a nullable enum ("BLOCK" | "AUTO_SWITCH"). Both travel verbatim in
  // the apiGet/apiPut of `worker`, so binding the inputs is enough for read/write.
  const onToggleSingleMachinePresence = (event) => {
    const enabled = event.target.checked
    // On disable we clear the mode to null (backend contract); on re-enable it stays null, so the
    // user must pick a mode again (the previous one is not remembered).
    setWorker({
      ...worker,
      singleMachinePresence: enabled,
      singleMachinePresenceMode: enabled ? worker.singleMachinePresenceMode : null
    })
  }

  // The mode is mandatory when the restriction is active — mirrors the backend CHECK that rejects
  // singleMachinePresence=true with singleMachinePresenceMode=null.
  const singleMachinePresenceModeMissing = worker.singleMachinePresence === true && !worker.singleMachinePresenceMode

  const saveWorker = () => {
    apiPut('worker', worker)
      .then(() => {
        changeBetweenVisualizeModes(false)
        goBack('EDIT')
      })
      .catch((error) => {
        showAlert({
          title: '',
          message: error?.message || 'Unexpected error',
          severity: 'error'
        })
      })
  }

  // ⋮ menu in the "Availability & skill" header (D11): opens the Skills modal. stopPropagation
  // keeps the click from also toggling the accordion's own expand/collapse.
  const openSkillsMenu = (event) => {
    event.stopPropagation()
    setSkillsMenuAnchorEl(event.currentTarget)
  }
  const closeSkillsMenu = () => setSkillsMenuAnchorEl(null)
  const openSkillsDialog = () => {
    closeSkillsMenu()
    setSkillsDialogOpen(true)
  }
  const closeSkillsDialog = () => setSkillsDialogOpen(false)
  // Refetch the worker after the modal saves so this screen's own state (and a subsequent click
  // on this form's own Save button) never overwrites the just-edited flags with stale ones.
  const onSkillsDialogSaved = () => {
    fetchWorker()
  }

  const getTotalLayout = () => {
    if (worker.id) {
      return (
        <div>
          {AlertElement}
          <DefaultAccordion title={t_('Identification')}>
            <Grid container direction='row' spacing={2} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }}>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label={t_('Code')}
                  id={worker.code}
                  value={worker.code}
                  onChange={(event) => {
                    setWorker({ ...worker, code: event.target.value })
                  }}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label={t_('Name')}
                  id={worker.name}
                  value={worker.name}
                  onChange={(event) => {
                    setWorker({ ...worker, name: event.target.value })
                  }}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label={t_('Last name')}
                  id={worker.surname}
                  value={worker.surname}
                  onChange={(event) => {
                    setWorker({ ...worker, surname: event.target.value })
                  }}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label={t_('Phone')}
                  id={worker.tlf}
                  value={worker.tlf}
                  onChange={(event) => {
                    setWorker({ ...worker, tlf: event.target.value })
                  }}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label={t_('Email')}
                  id={worker.email}
                  value={worker.email}
                  onChange={(event) => {
                    setWorker({ ...worker, email: event.target.value })
                  }}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>

          </DefaultAccordion>
          <DefaultAccordion
            title={t_('Availability & skill')}
            headerRender={
              // D12: disabled while the main form is in edit mode — the modal's immediate save
              // must not race with unsaved changes to this same worker on the form itself.
              <IconButton aria-label={t_('Skills')} onClick={openSkillsMenu} disabled={editMode}>
                <MoreVert />
              </IconButton>
            }
          >
            <Grid container direction='row' spacing={4} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }}>
              <Grid item container direction='row' xs={2}>
                <Grid item xs={11}>
                  <TextField
                    select
                    label={t_('Calendar')}
                    fullWidth
                    value={worker.calendar.id}
                    disabled={!editMode}
                    onChange={onChangeCalendarValues}
                  >
                    {calendars.map((calendar) => (
                      <MenuItem value={calendar.id} key={calendar.id}>
                        {calendar.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1} mt={1}>
                  <IconButton
                    color='primary'
                    variant='outlined'
                    disabled={editMode}
                    onClick={() => navigate(generatePath('/configuration/calendarInstance/:id', { id: worker?.calendar?.idInstance }))}
                  >
                    <CalendarMonthIcon />
                  </IconButton>
                </Grid>

              </Grid>

              <Grid item xs={4}>
                <SmartSelect
                  multiple
                  selectableOptions={optionTechnologiesMultiSelect()}
                  value={selectTechnologiesOrGroups(false)}
                  onChange={onChangeTechnologyValues}
                  label={t_('Select skills')}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={4}>
                <SmartSelect
                  multiple
                  selectableOptions={optionTechnologyGroupsMultiSelect()}
                  value={selectTechnologiesOrGroups(true)}
                  onChange={onChangeTechnologyGroupValues}
                  label={t_('Select skill groups')}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={4}>
                <SmartSelect
                  multiple
                  selectableOptions={mosCapabilityOptions}
                  value={selectedMosCapabilities}
                  onChange={onChangeMosCapabilities}
                  label={t_('Select MOS capabilities')}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
            <div>
              <Paper style={{ marginTop: '16px', padding: '16px' }}>
                <h5>{t_('Selected individual skills')}</h5>
                {[...new Set(selectedTechnologies
                // .filter((tech) => !tech.isGroup)
                  .concat(selectedGroupTechnologies)
                  .map((tech) => tech.name))].sort()
                  .map((techName, index) => (
                    <Chip
                      key={index}
                      label={techName}
                      variant='outlined'
                      style={{ margin: '4px' }}
                    />
                  ))}
              </Paper>
            </div>
            <div>
              <Paper style={{ marginTop: '16px', padding: '16px' }}>
                <h5>{t_('Selected MOS capabilities')}</h5>
                {selectedMosCapabilities.map((capabilityName, index) => (
                  <Chip
                    key={index}
                    label={capabilityName}
                    variant='outlined'
                    style={{ margin: '4px' }}
                  />
                ))}
              </Paper>
            </div>
          </DefaultAccordion>
          <DefaultAccordion title={t_('Machine presence')}>
            <Grid container direction='row' spacing={2} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!worker.singleMachinePresence}
                      onChange={onToggleSingleMachinePresence}
                      disabled={!editMode}
                    />
                  }
                  label={t_('Restrict to a single machine at a time')}
                />
              </Grid>
              {worker.singleMachinePresence && (
                <Grid item xs={4}>
                  <TextField
                    select
                    fullWidth
                    label={t_('Presence mode')}
                    value={worker.singleMachinePresenceMode || ''}
                    disabled={!editMode}
                    error={editMode && singleMachinePresenceModeMissing}
                    helperText={editMode && singleMachinePresenceModeMissing ? t_('Select a mode') : ''}
                    onChange={(event) => {
                      setWorker({ ...worker, singleMachinePresenceMode: event.target.value })
                    }}
                  >
                    <MenuItem value='BLOCK'>{t_('Block the second login')}</MenuItem>
                    <MenuItem value='AUTO_SWITCH'>{t_('Close the previous presence and open the new one')}</MenuItem>
                  </TextField>
                </Grid>
              )}
            </Grid>
          </DefaultAccordion>
          <ButtonBackSave
            editControl={() => changeBetweenVisualizeModes(true)}
            backButtonAction={() => goBack('BACK')}
            saveButtonAction={() => saveWorker()}
            saveDisabled={singleMachinePresenceModeMissing}
            ref={backSaveButtonRef}
          />
        </div>
      )
    } else {
      return <CircularProgress />
    }
  }

  return (
    <div>
      {getTotalLayout()}
      <Menu anchorEl={skillsMenuAnchorEl} open={Boolean(skillsMenuAnchorEl)} onClose={closeSkillsMenu}>
        <MenuItem onClick={openSkillsDialog}>{t_('Skills')}</MenuItem>
      </Menu>
      <SkillCapabilitiesDialog
        open={skillsDialogOpen}
        workerId={workerId}
        onClose={closeSkillsDialog}
        onSaved={onSkillsDialogSaved}
      />
    </div>
  )
}

export default WorkerSettings
